import React, { useState } from 'react';
import axios from 'axios';
import './ChannelPost.css';

function formatTimeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function renderSenderInfo(sender) {
  if (!sender) return { avatar: <span aria-hidden="true">👤</span>, name: 'Anonymous', detail: null };

  const level = sender.disclosure_level;

  if (sender.is_self) {
    return {
      avatar: sender.photo_url ? <img src={sender.photo_url} alt="You" /> : <span>👤</span>,
      name: <>{sender.display_name} <span className="channel-post-you-badge">You</span></>,
      detail: null
    };
  }

  if (sender.is_deleted) {
    return { avatar: <span>👻</span>, name: 'Deleted User', detail: null };
  }

  if (level === -1 || level === undefined) {
    const locationParts = [sender.location, sender.hometown ? `from ${sender.hometown}` : null].filter(Boolean);
    const detail = locationParts.length > 0 ? locationParts.join(' · ') : null;
    return { avatar: <span>👤</span>, name: 'Anonymous', detail };
  }

  if (level === 0) {
    return { avatar: <span>👤</span>, name: 'Anonymous', detail: sender.location || null };
  }

  if (level === 1) {
    return {
      avatar: sender.photo_url ? <img src={sender.photo_url} alt={sender.display_name} /> : <span>👤</span>,
      name: sender.display_name,
      detail: null
    };
  }

  if (level === 2) {
    const details = [sender.profession, sender.hometown].filter(Boolean).join(' · ');
    return {
      avatar: sender.photo_url ? <img src={sender.photo_url} alt={sender.display_name} /> : <span>👤</span>,
      name: sender.display_name,
      detail: details || null
    };
  }

  if (level === 3) {
    const details = [sender.profession, sender.hometown].filter(Boolean).join(' · ');
    const contact = sender.contact ? [sender.contact.email, sender.contact.phone].filter(Boolean).join(' · ') : null;
    return {
      avatar: sender.photo_url ? <img src={sender.photo_url} alt={sender.display_name} /> : <span>👤</span>,
      name: sender.display_name,
      detail: [details, contact].filter(Boolean).join(' | ')
    };
  }

  return { avatar: <span>👤</span>, name: 'Anonymous', detail: null };
}

function ChannelPost({ post, channelId, onLike, onConnect, onReport, onDelete, onEdit, onCommentAdded, isAdmin }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [adminEditing, setAdminEditing] = useState(false);
  const [adminEditContent, setAdminEditContent] = useState('');

  const sender = post.sender || {};
  const senderInfo = post.is_ai_generated
    ? { avatar: <span>🌱</span>, name: 'Belong', detail: null }
    : renderSenderInfo(sender);
  const { avatar, name, detail } = senderInfo;
  const token = localStorage.getItem('accessToken');
  const headers = { Authorization: `Bearer ${token}` };

  const toggleComments = async () => {
    if (!commentsOpen && comments.length === 0) {
      setLoadingComments(true);
      try {
        const res = await axios.get(
          `/api/channels/${channelId}/posts/${post.post_id}/comments`,
          { headers }
        );
        setComments(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Error loading comments:', err);
      } finally {
        setLoadingComments(false);
      }
    }
    setCommentsOpen(!commentsOpen);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setSubmitting(true);
    setCommentError('');
    try {
      const res = await axios.post(
        `/api/channels/${channelId}/posts/${post.post_id}/comments`,
        { content: newComment.trim() },
        { headers }
      );
      setComments(prev => [...prev, res.data]);
      setNewComment('');
      if (onCommentAdded) onCommentAdded(post.post_id);
    } catch (err) {
      setCommentError(err.response?.data?.error || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdminSave = async () => {
    if (adminEditContent.trim().length < 50) {
      alert('Content must be at least 50 characters');
      return;
    }
    try {
      await axios.put(
        `/api/admin/content-review/${post.post_id}/edit`,
        { content: adminEditContent.trim() },
        { headers }
      );
      post.content = adminEditContent.trim();
      setAdminEditing(false);
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div className="channel-post">
      <div className="channel-post-header">
        <div className="channel-post-avatar">{avatar}</div>
        <div className="channel-post-sender">
          <div className="channel-post-sender-name">
            {name}
            {post.is_ai_generated && (
              <span className="channel-post-ai-badge">Community Resource</span>
            )}
          </div>
          {detail && <div className="channel-post-sender-detail">{detail}</div>}
        </div>
        <span className="channel-post-time">
          {formatTimeAgo(post.created_at)}
          {post.is_edited && <span className="channel-post-edited"> · edited</span>}
        </span>
      </div>

      {post.category && post.category !== 'general' && (
        <span className="channel-post-category-badge">{post.category}</span>
      )}

      {post.title && (
        <div className="channel-post-title" style={{ fontSize: '17px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
          {post.title}
        </div>
      )}
      <div className="channel-post-content">
        {adminEditing ? (
          <div>
            <textarea
              value={adminEditContent}
              onChange={(e) => setAdminEditContent(e.target.value)}
              style={{ width: '100%', minHeight: '150px', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '14px', lineHeight: '1.6', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button onClick={handleAdminSave} style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: '13px' }}>Save</button>
              <button onClick={() => setAdminEditing(false)} style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
            </div>
          </div>
        ) : (
          post.content
        )}
      </div>

      <div className="channel-post-actions">
        <button
          className={`channel-post-action-btn ${post.user_liked ? 'liked' : ''}`}
          onClick={() => onLike && onLike(post.post_id)}
          aria-label={post.user_liked ? 'Unlike' : 'Like'}
        >
          {post.user_liked ? '❤️' : '🤍'} {post.like_count || 0}
        </button>

        <button
          className={`channel-post-action-btn ${commentsOpen ? 'active-comment' : ''}`}
          onClick={toggleComments}
          aria-label="Toggle comments"
        >
          💬 {post.comment_count || 0}
        </button>

        {sender.connect_available && !post.is_ai_generated && (
          <button
            className="channel-post-connect-btn"
            onClick={() => onConnect && onConnect(post)}
          >
            🤝 Connect
          </button>
        )}

        <div className="channel-post-menu">
          <button
            className="channel-post-menu-btn"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="More options"
          >
            ⋯
          </button>
          {menuOpen && (
            <div className="channel-post-dropdown">
              {sender.is_self && (
                <>
                  <button onClick={() => { setMenuOpen(false); onEdit && onEdit(post); }}>✏️ Edit</button>
                  <button className="danger" onClick={() => { setMenuOpen(false); onDelete && onDelete(post.post_id); }}>🗑️ Delete</button>
                </>
              )}
              {!sender.is_self && isAdmin && post.is_ai_generated && (
                <button onClick={() => { setMenuOpen(false); setAdminEditContent(post.content); setAdminEditing(true); }}>✏️ Edit Post</button>
              )}
              {!sender.is_self && (
                <button onClick={() => { setMenuOpen(false); onReport && onReport(post.post_id); }}>🚩 Report</button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Comments section */}
      {commentsOpen && (
        <div className="channel-post-comments">
          {loadingComments ? (
            <div className="channel-post-comments-loading">Loading comments...</div>
          ) : comments.length === 0 ? (
            <div className="channel-post-comments-empty">No comments yet. Be the first to reply.</div>
          ) : (
            <div className="channel-post-comments-list">
              {comments.map(comment => {
                const cs = renderSenderInfo(comment.sender);
                return (
                  <div key={comment.comment_id} className="channel-comment">
                    <div className="channel-comment-avatar">{cs.avatar}</div>
                    <div className="channel-comment-body">
                      <div className="channel-comment-header">
                        <span className="channel-comment-name">{cs.name}</span>
                        <span className="channel-comment-time">{formatTimeAgo(comment.created_at)}</span>
                      </div>
                      <div className="channel-comment-content">{comment.content}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <form className="channel-comment-form" onSubmit={handleSubmitComment}>
            <input
              type="text"
              placeholder="Write a reply..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              maxLength={500}
              disabled={submitting}
            />
            <button type="submit" disabled={submitting || !newComment.trim()}>
              {submitting ? '...' : 'Reply'}
            </button>
          </form>
          {commentError && <div className="channel-comment-error">{commentError}</div>}
        </div>
      )}
    </div>
  );
}

export default ChannelPost;
